import { LaneLine, Vector2 } from "@app/shared";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ICoordsHelper, MapContext, MapObjArray, animateCircle, createGooglePolyline, createVertexCircle, debounce, useFacilityCoordsHelper } from "../../../../helpers";
import { usePolygonScaleAndRotation } from "../../../../hooks";
import { MapShapeProps } from "../MapShape";

export interface LaneLineMapShapeProps extends MapShapeProps<LaneLine> {
    color?: string;
    showLabel?: boolean;
    /** Draw a dark casing line underneath the colored path for legibility (used for target tracks). */
    cased?: boolean;
    /** Stroke opacity 0..1 for the path + casing (used to fade older target tracks). */
    opacity?: number;
}
export const LaneLineMapShape: React.FC<LaneLineMapShapeProps> = ({ facility, shape, isEditable, isSelectable, onUpdate, onClick, isSelected, color, showLabel, cased, opacity }) => {
    const shouldShowEditingControls = (shape: LaneLine | undefined) => {
        if (!shape) return false;
        return shape.isExplicitlyCompleted === true || (shape.id && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(shape.id));
    };

    const isClickableForSelection = (shape: LaneLine | undefined) => {
        if (!shape) return false;
        return (shape.points?.length ?? 0) >= 2;
    };
    // Turn Type - Straight, left, Right, UTurn - null
    // Lane Number - needs to be positive, above 0, or null
    const [parentMap, setBounds] = useContext(MapContext);
    const listeners = useRef([] as Array<google.maps.MapsEventListener>);
    const [polyLaneLine, setpolyLaneLine] = useState<google.maps.Polyline>();
    const casingLine = useRef<google.maps.Polyline>();
    const mapObjects = useRef([] as MapObjArray);
    const vertexCircles = useRef([] as Array<google.maps.Circle>);
    const didMountRef = useRef(false);
    const fCoordsHelper = useFacilityCoordsHelper(facility);
    const label = useRef<google.maps.marker.AdvancedMarkerElement>();

    const DEFAULT_COLOR = "green";

    useEffect(() => {
        //create/update google poly for shape
        if (parentMap && shape?.points?.length) {
            const { polyline: poly, casing, label: polyLabel, cleanup } = initPoly(shape, parentMap, fCoordsHelper, color ?? DEFAULT_COLOR, showLabel ? shape.name : undefined);
            setpolyLaneLine(poly);
            casingLine.current = casing;
            label.current = polyLabel;
            setBounds((curr) => {
                poly?.getPath()
                    .getArray()
                    .forEach((pt) => curr.extend(pt));
                return curr;
            });
            return () => {
                cleanup();
                mapObjects.current?.forEach((mo) => {
                    if ('setVisible' in mo) {
                        mo.setVisible(false);
                        mo.setMap(null);
                    } else {
                        (mo as google.maps.marker.AdvancedMarkerElement).map = null;
                    }
                });
                setpolyLaneLine(undefined);
                casingLine.current = undefined;
                label.current = undefined;
                mapObjects.current = [];
            };
        }
    }, [parentMap, shape, color, fCoordsHelper, setBounds, showLabel, cased]);

    // Fade the path + its casing in place (avoids tearing down/rebuilding the polyline as ranks shift).
    useEffect(() => {
        if (opacity == null) return;
        polyLaneLine?.setOptions({ strokeOpacity: opacity });
        casingLine.current?.setOptions({ strokeOpacity: opacity });
    }, [opacity, polyLaneLine]);

    const handleClick = (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || !shape) {
            return;
        }

        if (e.domEvent && (e.domEvent as MouseEvent).button !== 0) {
            return;
        }

        const dz = shape;
        const v2 = fCoordsHelper.fromLatLng(e.latLng);
        if (!dz.points?.length) {
            dz.points = [v2];
        } else {
            dz.points.push(v2);
        }
        onUpdate?.(Object.assign(new LaneLine(), { ...shape, points: dz.points }));
    };

    const memoHandleClick = useCallback(handleClick, [shape, onUpdate]); //eslint-disable-line react-hooks/exhaustive-deps

    const handleMiddleClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!shape || shape.isComplete()) return;

            // check if it's a middle mouse button click
            if (e.domEvent && (e.domEvent as MouseEvent).button === 1) {
                // lane line needs at least 2 points to be completed
                if (shape.points && shape.points.length >= 2) {
                    e.stop?.();
                    // preventing bubbling
                    e.domEvent?.preventDefault?.();
                    e.domEvent?.stopPropagation?.();
                    e.domEvent?.stopImmediatePropagation?.();

                    // Create the completed lane line
                    const completedLaneLine = Object.assign(new LaneLine(), {
                        ...shape,
                        isExplicitlyCompleted: true,
                    });

                    if (onUpdate) {
                        (onUpdate as any)(completedLaneLine, false, true); // shape, shouldDelete=false, wasCompletedByMiddleClick=true
                    }

                    return;
                }
            }
        },
        [shape, onUpdate]
    );

    const memoHandleMiddleClick = useCallback(handleMiddleClick, [handleMiddleClick]);

    const handleDoubleClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!shape || shape.isComplete()) return;

            const points = [...(shape.points ?? [])];
            while (points.length >= 2) {
                const a = points[points.length - 1];
                const b = points[points.length - 2];
                if (a && b && Math.hypot(a.x - b.x, a.y - b.y) < 0.3) {
                    points.pop();
                } else {
                    break;
                }
            }

            if (points.length < 2) return;

            e.stop?.();
            e.domEvent?.preventDefault?.();
            e.domEvent?.stopPropagation?.();

            const completedLaneLine = Object.assign(new LaneLine(), {
                ...shape,
                points,
                isExplicitlyCompleted: true,
            });
            (onUpdate as any)?.(completedLaneLine, false, true);
        },
        [shape, onUpdate]
    );

    const memoHandleDoubleClick = useCallback(handleDoubleClick, [handleDoubleClick]);

    const handleMove = (e: google.maps.MapMouseEvent) => {
        if (!polyLaneLine || !isEditable || !e.latLng) return;

        const path = polyLaneLine.getPath().getArray();
        if (shape?.points?.length !== undefined && shape.points.length < path.length) {
            path.pop();
        }

        polyLaneLine.setPath([...path, e.latLng]);
    };
    const memoHandleMove = useCallback(handleMove, [polyLaneLine, isEditable, shape]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        //editable
        if (isEditable && parentMap) {
            if (!shouldShowEditingControls(shape)) {
                listeners.current.push(parentMap.addListener("click", memoHandleClick));
                listeners.current.push(parentMap.addListener("mousedown", memoHandleMiddleClick));
                listeners.current.push(parentMap.addListener("dblclick", memoHandleDoubleClick));
                listeners.current.push(parentMap.addListener("mouseout", () => polyLaneLine?.setPath(shape?.points?.map(fCoordsHelper.toLatLng) ?? [])));
                listeners.current.push(parentMap.addListener("mousemove", memoHandleMove));
            } else if (polyLaneLine) {
                vertexCircles.current = polyLaneLine
                    .getPath()
                    .getArray()
                    .map((pos, idx) => {
                        const isClickable = true;
                        const posMark = createVertexCircle(pos, parentMap, isClickable, color ?? DEFAULT_COLOR);
                        posMark.addListener("dragend", (d: google.maps.MapMouseEvent) => {
                            if (d.latLng && polyLaneLine) {
                                const path = polyLaneLine.getPath();
                                path.setAt(idx, d.latLng);
                                vertexCircles.current[idx]?.setCenter(d.latLng);
                                const points = path.getArray().map(fCoordsHelper.fromLatLng);
                                onUpdate?.(Object.assign(new LaneLine(), { ...shape, points: points }));
                            }
                        });
                        posMark.addListener("drag", (d: google.maps.MapMouseEvent) => {
                            if (d.latLng && polyLaneLine) {
                                const path = polyLaneLine.getPath();
                                path.setAt(idx, d.latLng);
                                polyLaneLine?.setPath(path);
                            }
                        });

                        return posMark;
                    });
                vertexCircles.current.forEach((vc) => mapObjects.current.push(vc));
                polyLaneLine.setDraggable(true);
                listeners.current.push(polyLaneLine.addListener("dragstart", handlePolyDragStart));
                listeners.current.push(polyLaneLine.addListener("dragend", handlePolyDragEnd));
            }
        }

        return () => {
            listeners.current.forEach((l) => l?.remove());
            listeners.current = [];
            vertexCircles.current.forEach((l) => {
                l.setVisible(false);
                l.setMap(null);
            });
            vertexCircles.current = [];
        };
    }, [isEditable, shape, parentMap, memoHandleMove, memoHandleClick, memoHandleMiddleClick, memoHandleDoubleClick]); //eslint-disable-line react-hooks/exhaustive-deps

    const debouncedUpdate = debounce(() => {
        if (onUpdate && polyLaneLine && shape) {
            onUpdate?.(Object.assign(new LaneLine(), { ...shape, points: polyLaneLine.getPath().getArray().map(fCoordsHelper.fromLatLng) }));
        }
    }, 500);

    usePolygonScaleAndRotation(
        parentMap,
        polyLaneLine,
        () => !!(isEditable && shape && polyLaneLine && isSelected),
        debouncedUpdate,
        () => [vertexCircles.current],
        [polyLaneLine, isEditable, shape, isSelected, vertexCircles]
    );

    const handlePolyDragStart = () => {
        vertexCircles.current.forEach((vc) => vc.setVisible(false));
    };

    const handlePolyDragEnd = (e: google.maps.PolyMouseEvent) => {
        const points = polyLaneLine?.getPath().getArray().map(fCoordsHelper.fromLatLng);
        onUpdate?.(Object.assign(new LaneLine(), { ...shape, points: points }));
    };

    useEffect(() => {
        // handle selection from list
        if (!isSelected || !polyLaneLine || !shape?.isComplete() || !didMountRef.current) {
            didMountRef.current = true;
            return;
        }
    }, [isSelected, parentMap]);

    useEffect(() => {
        //selection from map
        if (!isSelectable) {
            return;
        }
        let click: google.maps.MapsEventListener;

        if (onClick && parentMap && polyLaneLine && shape && isClickableForSelection(shape)) {
            polyLaneLine.set("clickable", true);
            click = polyLaneLine.addListener("click", (e: google.maps.MapMouseEvent) => onClick?.(e, shape));
        }
        return () => {
            polyLaneLine?.set("clickable", false);
            click?.remove();
        };
    }, [onClick, parentMap, polyLaneLine, shape, isSelectable]);
    return null;

    function initPoly(shape: LaneLine, parentMap: google.maps.Map, coordsHelper: ICoordsHelper, color: string, labelText?: string) {
        shape.points = populateMissingVectors(shape);

        const isDrawing = !shouldShowEditingControls(shape);
        const drawingOptions: google.maps.PolylineOptions = {
            strokeColor: color,
            strokeOpacity: 0,
            icons: [
                {
                    icon: { path: "M 0,-1 0,1", strokeColor: color, strokeOpacity: 1, strokeWeight: 3, scale: 3 },
                    offset: "0",
                    repeat: "12px",
                },
            ],
        };
        const completedOptions: google.maps.PolylineOptions = {
            strokeColor: color,
            strokeOpacity: opacity ?? 1,
            icons: [
                {
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: cased ? 3 : 4,
                        fillColor: color,
                        fillOpacity: opacity ?? 1,
                        strokeColor: cased ? "#1A1A1A" : undefined,
                        strokeWeight: cased ? 1.5 : 1,
                    },
                    // Target tracks: repeat direction arrows along the whole length so heading reads at a
                    // glance. Lane lines keep a single arrow at the end (default offset 100%).
                    ...(cased ? { offset: "0", repeat: "70px" } : {}),
                },
            ],
        };

        return createGooglePolyline(shape.points ?? [], coordsHelper, parentMap, isDrawing ? drawingOptions : completedOptions, labelText, cased ? { color: "#1A1A1A", extraWeight: 3 } : undefined);
    }

    function populateMissingVectors(shape: LaneLine): Vector2[] {
        if (!shape.points) return [];
        return shape.points.map((point) => point ?? new Vector2());
    }
};
